import { UUID } from "io-ts-types/UUID";

import { FundraiserResponse } from "@every.org/common/src/codecs/entities";
import { LONG_DESCRIPTION_MAX_LENGTH } from "@every.org/common/src/codecs/nonprofit";
import { SafeInt } from "@every.org/common/src/codecs/number";
import { Currency } from "@every.org/common/src/entity/types";
import { checkYoutubeVideoUrlForPatternMatch } from "@every.org/common/src/helpers/socialNetworks";
import {
  deleteFundraiserRouteSpec,
  hideFundraiserRouteSpec,
  leaveFundraiserRouteSpec,
  postFundraiserRouteSpec,
  unhideFundraiserRouteSpec,
  updateFundraiserRouteSpec,
} from "@every.org/common/src/routes/fundraiser";

import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import FundraiserValidationError from "src/pages/Fundraiser/EditForm/FundraiserValidationError";
import { FundraiserMatchCampaignEdit } from "src/pages/Fundraiser/EditForm/MatchCampaignCard";
import {
  PostFundraiser,
  UpdateFundraiser,
} from "src/pages/Fundraiser/EditForm/types";
import { queryApi } from "src/utility/apiClient";
import { logger } from "src/utility/logger";

export const FUNDRAISER_TITLE_MAX_LENGTH = 60;
export const FUNDRAISER_TITLE_MIN_LENGTH = 2;

const defaultCreationBody = {
  currency: Currency.USD,
  active: true,
  hidden: false,
  pinned: false,
  parentFundraiserId: null,
  startDate: null,
  endDate: null,
  goal: null,
  raisedOffline: null,
};

export function createValidation(params: {
  title?: string;
  description?: string | null;
  coverYoutubeVideoUrl?: string | null;
  matchCampaign?: FundraiserMatchCampaignEdit;
  maxMatchPerDonationOneTimeAmount?: SafeInt | undefined;
  hasCoverOption?: boolean;
}) {
  return {
    title: !params.title?.length
      ? {
          success: false,
          message: "Title cannot be empty",
        }
      : params.title.length > FUNDRAISER_TITLE_MAX_LENGTH
      ? {
          success: false,
          message: `Max title length is ${FUNDRAISER_TITLE_MAX_LENGTH}`,
        }
      : params.title.length < FUNDRAISER_TITLE_MIN_LENGTH
      ? {
          success: false,
          message: `Min title length is ${FUNDRAISER_TITLE_MIN_LENGTH}`,
        }
      : undefined,
    description: !params.description?.length
      ? {
          success: false,
          message: "Description cannot be empty",
        }
      : params.description.length > LONG_DESCRIPTION_MAX_LENGTH
      ? {
          success: false,
          message: `Max description length is ${LONG_DESCRIPTION_MAX_LENGTH}`,
        }
      : undefined,
    coverYoutubeVideoUrl:
      params.coverYoutubeVideoUrl &&
      !checkYoutubeVideoUrlForPatternMatch(params.coverYoutubeVideoUrl)
        ? {
            success: false,
            message: "Youtube video url is invalid",
          }
        : undefined,
    hasCoverOption:
      params.hasCoverOption != undefined && !params.hasCoverOption
        ? {
            // double check logo
            success: false,
            message: "Your fundraiser needs a photo or video",
          }
        : undefined,
    // do we want to enforce the validation of photo or video for all fundraiser, not just multi?
  };
}

export async function postFundraiser(
  nonprofitId: UUID,
  { title, description, metadata, ...rest }: PostFundraiser,
  nonprofit?: ContextNonprofit,
  forNonprofits?: string[],
  userId?: string
): Promise<FundraiserResponse> {
  const forNonprofitUUIDs = forNonprofits?.filter(
    (id): id is UUID => UUID.decode(id)._tag === "Right"
  );

  const { fundraiser } = await queryApi(postFundraiserRouteSpec, {
    routeTokens: {},
    queryParams: {},
    body: {
      ...defaultCreationBody,
      title,
      description,
      nonprofitId,
      metadata,
      ...rest,
      ...(forNonprofitUUIDs ? { forNonprofits: forNonprofitUUIDs } : {}),
    },
  });

  return fundraiser;
}

export async function updateFundraiser(
  fundraiserId: UUID,
  nonprofitId: UUID,
  { title, description, metadata, ...rest }: UpdateFundraiser,
  nonprofit?: ContextNonprofit,
  userId?: string
): Promise<FundraiserResponse> {
  if (
    (typeof title === "string" && !title.length) ||
    (title?.length || 0) > FUNDRAISER_TITLE_MAX_LENGTH ||
    (title?.length || FUNDRAISER_TITLE_MIN_LENGTH) <
      FUNDRAISER_TITLE_MIN_LENGTH ||
    (typeof description === "string" && !description.length) ||
    (description?.length || 0) > LONG_DESCRIPTION_MAX_LENGTH ||
    (metadata?.coverYoutubeVideoUrl &&
      !checkYoutubeVideoUrlForPatternMatch(metadata?.coverYoutubeVideoUrl))
  ) {
    throw new FundraiserValidationError({
      message: "Please fix the errors above and resubmit",
      validation: createValidation({
        title,
        description,
        coverYoutubeVideoUrl: metadata?.coverYoutubeVideoUrl,
      }),
    });
  }

  const body = {
    title,
    description,
    metadata,
    ...rest,
  };

  const response = await queryApi(updateFundraiserRouteSpec, {
    routeTokens: {
      fundraiserIdentifier: fundraiserId,
      nonprofitIdentifier: nonprofitId,
    },
    queryParams: {},
    body,
  });

  return response.fundraiser;
}

export async function deleteFundraiser(fundraiserId: UUID, nonprofitId: UUID) {
  try {
    await queryApi(deleteFundraiserRouteSpec, {
      routeTokens: {
        fundraiserIdentifier: fundraiserId,
        nonprofitIdentifier: nonprofitId,
      },
      queryParams: {},
      body: {},
    });
  } catch (error) {
    logger.error({
      error,
      message: "Failed to delete Fundraiser",
      data: { fundraiserId },
    });
  }
  return;
}

export async function hideFundraiser(fundraiserId: UUID, nonprofitId: UUID) {
  try {
    return await queryApi(hideFundraiserRouteSpec, {
      routeTokens: {
        fundraiserIdentifier: fundraiserId,
        nonprofitIdentifier: nonprofitId,
      },
      queryParams: {},
      body: {},
    });
  } catch (error) {
    logger.error({
      error,
      message: "Failed to hide Fundraiser",
      data: { fundraiserId },
    });
  }
  return;
}

export async function unhideFundraiser(fundraiserId: UUID, nonprofitId: UUID) {
  try {
    return await queryApi(unhideFundraiserRouteSpec, {
      routeTokens: {
        fundraiserIdentifier: fundraiserId,
        nonprofitIdentifier: nonprofitId,
      },
      queryParams: {},
      body: {},
    });
  } catch (error) {
    logger.error({
      error,
      message: "Failed to unhide Fundraiser",
      data: { fundraiserId },
    });
  }
  return;
}

export async function leaveFundraiser(
  fundraiserId: UUID,
  nonprofitId: UUID
): Promise<FundraiserResponse> {
  try {
    const response = await queryApi(leaveFundraiserRouteSpec, {
      routeTokens: {
        fundraiserIdentifier: fundraiserId,
        nonprofitIdentifier: nonprofitId,
      },
      queryParams: {},
      body: {},
    });
    return response;
  } catch (error) {
    logger.error({
      error,
      message: "Failed to leave Fundraiser",
      data: { fundraiserId },
    });
    throw error;
  }
}
